import { FileDocumentType } from '@nkr/enums/document-type';
import { FindHint, FindHintLocateKind, FindMode } from '../types';

export type DocumentVerifyKind =
    | 'tax'
    | 'w2'
    | 'paystub'
    | 'social_security'
    | 'affidavit'
    | 'expense'
    | 'generic';

export interface FindHintSource {
    name?: string;
    firstName?: string;
    lastName?: string;
    alias?: string;
    donorAlias?: string;
    ssnDecrypted?: string;
    birthDate?: string;
    birthdate?: string;
    incomeIrsYear?: number;
    incomeIrsYearGross?: number;
    incomePayStubGross?: number;
    incomePayStubGrosses?: number[];
    incomePayStubStartDate?: string;
    incomePayStubEndDate?: string;
    email?: string;
    receiptAmount?: number;
    receiptDate?: string;
    employer?: string;
}

export interface BuildHintsOptions {
    hourly?: boolean;
}

function formatMoney(n: number): string {
    if (n == null || isNaN(n) || n === 0) {
        return '';
    }
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDateMaybe(raw: any): string {
    if (!raw) {
        return '';
    }
    const s = String(raw);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
        return `${m[2]}/${m[3]}/${m[1]}`;
    }
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${mm}/${dd}/${d.getFullYear()}`;
    }
    return s;
}

function moneyVariants(n: number): string[] {
    if (n == null || isNaN(n) || n === 0) {
        return [];
    }

    const cents = Math.round(Math.abs(n) * 100);
    const negative = n < 0;
    const dollars = Math.floor(cents / 100);
    const remainder = cents % 100;
    const grouped = dollars.toLocaleString('en-US');
    const ungrouped = String(dollars);
    const centsStr = String(remainder).padStart(2, '0');
    const out: string[] = [];
    const add = (v: string) => {
        if (v && out.indexOf(v) < 0) {
            out.push(v);
        }
    };
    const addAmount = (body: string) => {
        const signed = negative ? '-' + body : body;

        add(signed);
        add('$' + signed);

        if (negative) {
            add('-$' + body);
        }
    };

    addAmount(ungrouped + '.' + centsStr);
    addAmount(grouped + '.' + centsStr);

    if (remainder === 0) {
        addAmount(ungrouped);
        addAmount(grouped);
    }

    return out;
}

function dateVariants(raw: any): string[] {
    const primary = formatDateMaybe(raw);
    const out: string[] = [];
    const add = (v: string) => {
        if (v && out.indexOf(v) < 0) {
            out.push(v);
        }
    };
    add(primary);
    const s = String(raw || '');
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
        const y = m[1];
        const mo = m[2];
        const d = m[3];
        add(`${mo}/${d}/${y}`);
        add(`${parseInt(mo, 10)}/${parseInt(d, 10)}/${y}`);
        add(`${y}-${mo}-${d}`);
    }
    return out;
}

function ssnParts(source: FindHintSource): { digits: string; last4: string; formatted: string; masked: string } {
    const digits = (source.ssnDecrypted || '').replace(/\D/g, '');
    const last4 = digits.length >= 4 ? digits.slice(-4) : '';
    const formatted = digits.length >= 9
        ? `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
        : '';
    const masked = last4 ? `XXX-XX-${last4}` : '';
    return { digits, last4, formatted, masked };
}

function pushField(
    out: FindHint[],
    id: string,
    label: string,
    values: string[],
    opts: {
        priority?: 'required' | 'supporting';
        detail?: string;
        pageHint?: number;
        matchMode?: FindMode;
        relatedTo?: string;
        locateKind?: FindHintLocateKind;
    }
) {
    const cleaned: string[] = [];
    for (let i = 0; i < values.length; i++) {
        const v = (values[i] || '').toString().trim();
        if (!v || v.length < 2) {
            continue;
        }
        if (cleaned.indexOf(v) < 0) {
            cleaned.push(v);
        }
    }
    if (!cleaned.length) {
        return;
    }
    if (out.some(h => h.id === id)) {
        return;
    }
    const hint: FindHint = {
        id,
        label,
        value: cleaned[0],
        detail: opts.detail || cleaned[0],
        variants: cleaned,
        pageHint: opts.pageHint,
        matchMode: opts.matchMode
    };
    if (opts.priority) {
        hint.priority = opts.priority;
    }
    if (opts.relatedTo) {
        hint.relatedTo = opts.relatedTo;
    }

    if (opts.locateKind) {
        hint.locateKind = opts.locateKind;
    }

    out.push(hint);
}

function addIdentity(
    out: FindHint[],
    source: FindHintSource,
    priority: 'required' | 'supporting'
) {
    pushField(out, 'name', 'Name', [source.name || ''], { priority, pageHint: 1 });
    pushField(out, 'firstName', 'First', [source.firstName || ''], {
        priority: 'supporting',
        pageHint: 1,
        relatedTo: priority === 'required' ? 'name' : undefined
    });
    pushField(out, 'lastName', 'Last', [source.lastName || ''], {
        priority: 'supporting',
        pageHint: 1,
        relatedTo: priority === 'required' ? 'name' : undefined
    });
}

function addSsn(
    out: FindHint[],
    source: FindHintSource,
    priority: 'required' | 'supporting',
    preferFull: boolean
) {
    const s = ssnParts(source);
    const spaced = s.digits.length >= 9
        ? `${s.digits.slice(0, 3)} ${s.digits.slice(3, 5)} ${s.digits.slice(5)}`
        : '';
    const spacedHyphen = s.digits.length >= 9
        ? `${s.digits.slice(0, 3)} - ${s.digits.slice(3, 5)} - ${s.digits.slice(5)}`
        : '';
    if (preferFull && s.formatted) {
        pushField(out, 'ssn', 'SSN', [s.formatted, s.digits, spaced, spacedHyphen].filter(Boolean), {
            priority,
            detail: s.formatted,
            pageHint: 1,
            matchMode: 'contains'
        });
    } else if (s.last4) {
        pushField(out, 'ssn', 'SSN', [s.masked, s.last4, s.formatted, s.digits, spaced, spacedHyphen].filter(Boolean), {
            priority,
            detail: s.formatted || `····${s.last4}`,
            pageHint: 1,
            matchMode: 'contains'
        });
    }
    addSsnLocateHelper(out);
}

function addSsnLocateHelper(out: FindHint[]) {
    if (out.some(h => h.id === 'ssnLocate')) {
        return;
    }
    out.push({
        id: 'ssnLocate',
        label: 'Pattern match',
        value: 'SSN',
        detail: 'SSN-shaped text on page (not the expected value)',
        priority: 'supporting',
        variants: ['SSN'],
        pageHint: 1,
        matchMode: 'contains',
        locateKind: 'ssnPattern',
        relatedTo: 'ssn'
    });
}

function addTaxYearGross(out: FindHint[], source: FindHintSource, priority: 'required' | 'supporting') {
    if (source.incomeIrsYear && source.incomeIrsYear > 1900) {
        pushField(out, 'irsYear', 'Tax year', [String(source.incomeIrsYear)], {
            priority,
            pageHint: 2,
            matchMode: 'contains'
        });
    }
    if (source.incomeIrsYearGross) {
        const variants = moneyVariants(source.incomeIrsYearGross);
        const pretty = formatMoney(source.incomeIrsYearGross);
        pushField(out, 'irsGross', 'IRS gross', variants, {
            priority,
            detail: pretty ? `$${pretty}` : variants[0],
            pageHint: 2,
            matchMode: 'contains',
            locateKind: 'money'
        });
    }
}

function addStubFields(
    out: FindHint[],
    source: FindHintSource,
    priority: 'required' | 'supporting'
) {
    pushField(out, 'stubStart', 'Stub start', dateVariants(source.incomePayStubStartDate), {
        priority,
        pageHint: 2,
        matchMode: 'contains'
    });
    pushField(out, 'stubEnd', 'Stub end', dateVariants(source.incomePayStubEndDate), {
        priority,
        pageHint: 2,
        matchMode: 'contains'
    });
    const stubGrosses = (source.incomePayStubGrosses && source.incomePayStubGrosses.length > 0)
        ? source.incomePayStubGrosses.filter(n => n != null && n > 0)
        : (source.incomePayStubGross ? [source.incomePayStubGross] : []);

    stubGrosses.forEach((gross, index) => {
        const variants = moneyVariants(gross);
        const pretty = formatMoney(gross);
        const id = stubGrosses.length === 1 ? 'stubGross' : `stubGross-${index + 1}`;
        const label = stubGrosses.length === 1 ? 'Stub gross' : `Stub ${index + 1} gross`;

        pushField(out, id, label, variants, {
            priority,
            detail: pretty ? `$${pretty}` : variants[0],
            pageHint: 2,
            matchMode: 'contains',
            locateKind: 'money'
        });
    });
}

function buildTaxLike(source: FindHintSource): FindHint[] {
    const out: FindHint[] = [];
    addIdentity(out, source, 'required');
    addSsn(out, source, 'required', true);
    addTaxYearGross(out, source, 'required');
    const bd = formatDateMaybe(source.birthDate || source.birthdate);
    pushField(out, 'dob', 'DOB', dateVariants(source.birthDate || source.birthdate), {
        priority: 'supporting',
        detail: bd,
        pageHint: 1,
        matchMode: 'contains'
    });
    if (source.employer) {
        pushField(out, 'employer', 'Employer', [source.employer], { priority: 'supporting', pageHint: 1 });
    }
    return out;
}

function buildW2(source: FindHintSource): FindHint[] {
    const out: FindHint[] = [];
    addIdentity(out, source, 'required');
    addSsn(out, source, 'required', true);
    addTaxYearGross(out, source, 'required');
    if (source.employer) {
        pushField(out, 'employer', 'Employer', [source.employer], { priority: 'supporting', pageHint: 1 });
    }
    return out;
}

function buildPaystub(source: FindHintSource, options?: BuildHintsOptions): FindHint[] {
    const out: FindHint[] = [];
    addIdentity(out, source, 'required');
    addStubFields(out, source, 'required');
    addSsn(out, source, options && options.hourly ? 'required' : 'supporting', false);
    if (source.employer) {
        pushField(out, 'employer', 'Employer', [source.employer], { priority: 'supporting', pageHint: 1 });
    }
    pushField(out, 'alias', 'Alias', [source.alias || ''], { priority: 'supporting', pageHint: 1 });
    return out;
}

function buildSocialSecurity(source: FindHintSource): FindHint[] {
    const out: FindHint[] = [];
    addIdentity(out, source, 'required');
    addSsn(out, source, 'required', true);
    if (source.incomeIrsYear && source.incomeIrsYear > 1900) {
        pushField(out, 'benefitYear', 'Benefit year', [String(source.incomeIrsYear)], {
            priority: 'required',
            pageHint: 2,
            matchMode: 'contains'
        });
    }
    if (source.incomeIrsYearGross) {
        const variants = moneyVariants(source.incomeIrsYearGross);
        const pretty = formatMoney(source.incomeIrsYearGross);
        pushField(out, 'benefitAmount', 'Benefit amount', variants, {
            priority: 'required',
            detail: pretty ? `$${pretty}` : variants[0],
            pageHint: 2,
            matchMode: 'contains',
            locateKind: 'money'
        });
    }
    return out;
}

function buildAffidavit(source: FindHintSource): FindHint[] {
    const out: FindHint[] = [];
    addIdentity(out, source, 'required');
    addSsn(out, source, 'required', false);
    addTaxYearGross(out, source, 'required');
    pushField(out, 'email', 'Email', [source.email || ''], { priority: 'supporting', pageHint: 2 });
    return out;
}

function buildExpense(source: FindHintSource): FindHint[] {
    const out: FindHint[] = [];
    if (source.receiptAmount) {
        const variants = moneyVariants(source.receiptAmount);
        const pretty = formatMoney(source.receiptAmount);
        pushField(out, 'receiptAmt', 'Amount', variants, {
            priority: 'required',
            detail: pretty ? `$${pretty}` : variants[0],
            matchMode: 'contains',
            locateKind: 'money'
        });
    }
    pushField(out, 'receiptDate', 'Date', dateVariants(source.receiptDate), {
        priority: 'required',
        matchMode: 'contains'
    });
    addIdentity(out, source, 'supporting');
    return out;
}

export function resolveVerifyKind(docType: FileDocumentType | string | null | undefined): DocumentVerifyKind {
    if (docType == null || docType === '') {
        return 'generic';
    }
    if (typeof docType === 'string') {
        const s = docType.toLowerCase();
        if (s === 'tax' || s === 'w2' || s === 'paystub' || s === 'social_security' || s === 'affidavit' || s === 'expense' || s === 'generic') {
            return s as DocumentVerifyKind;
        }
        return 'generic';
    }
    if (docType === FileDocumentType.Tax) {
        return 'tax';
    }
    if (docType === FileDocumentType.PayStub) {
        return 'paystub';
    }
    if (docType === FileDocumentType.SsnVerification) {
        return 'social_security';
    }
    if (docType === FileDocumentType.DsSelfEmployedAffidavit) {
        return 'affidavit';
    }
    if (docType === FileDocumentType.Receipts) {
        return 'expense';
    }
    return 'generic';
}

export function verifyKindLabel(kind: DocumentVerifyKind): string {
    if (kind === 'tax') {
        return 'Tax Document';
    }
    if (kind === 'w2') {
        return 'W-2';
    }
    if (kind === 'paystub') {
        return 'Pay Stub';
    }
    if (kind === 'social_security') {
        return 'Social Security';
    }
    if (kind === 'affidavit') {
        return 'Self Employment Affidavit';
    }
    if (kind === 'expense') {
        return 'Expense';
    }
    return 'Document';
}

export function buildHintsForDocument(
    kindOrType: DocumentVerifyKind | FileDocumentType | string | null | undefined,
    source: FindHintSource | null | undefined,
    options?: BuildHintsOptions
): FindHint[] {
    if (!source) {
        return [];
    }
    const kind = typeof kindOrType === 'number' || kindOrType == null || kindOrType === ''
        ? resolveVerifyKind(kindOrType as any)
        : (resolveVerifyKind(kindOrType as any));
    if (kind === 'tax') {
        return buildTaxLike(source);
    }
    if (kind === 'w2') {
        return buildW2(source);
    }
    if (kind === 'paystub') {
        return buildPaystub(source, options);
    }
    if (kind === 'social_security') {
        return buildSocialSecurity(source);
    }
    if (kind === 'affidavit') {
        return buildAffidavit(source);
    }
    if (kind === 'expense') {
        return buildExpense(source);
    }
    return buildFindHints(source);
}

export function buildFindHints(source: FindHintSource | null | undefined): FindHint[] {
    const out: FindHint[] = [];
    if (!source) {
        return out;
    }
    pushField(out, 'name', 'Name', [source.name || ''], {});
    pushField(out, 'firstName', 'First', [source.firstName || ''], {});
    pushField(out, 'lastName', 'Last', [source.lastName || ''], {});
    pushField(out, 'alias', 'Alias', [source.alias || ''], {});
    if (source.donorAlias && source.donorAlias !== source.alias) {
        pushField(out, 'donorAlias', 'Donor ID', [source.donorAlias], {});
    }
    const s = ssnParts(source);
    if (s.formatted) {
        pushField(out, 'ssn', 'SSN', [s.formatted, s.digits].filter(Boolean), {
            detail: s.formatted,
            matchMode: 'contains'
        });
    } else if (s.last4) {
        pushField(out, 'ssn', 'SSN', [s.masked, s.last4].filter(Boolean), {
            detail: `····${s.last4}`,
            matchMode: 'contains'
        });
    }
    addSsnLocateHelper(out);
    pushField(out, 'dob', 'DOB', dateVariants(source.birthDate || source.birthdate), {
        matchMode: 'contains'
    });
    if (source.incomeIrsYear && source.incomeIrsYear > 1900) {
        pushField(out, 'irsYear', 'Tax year', [String(source.incomeIrsYear)], {
            matchMode: 'contains'
        });
    }
    if (source.incomeIrsYearGross) {
        pushField(out, 'irsGross', 'IRS gross', moneyVariants(source.incomeIrsYearGross), {
            detail: `$${formatMoney(source.incomeIrsYearGross)}`,
            matchMode: 'contains',
            locateKind: 'money'
        });
    }
    if (source.incomePayStubGross) {
        pushField(out, 'stubGross', 'Stub gross', moneyVariants(source.incomePayStubGross), {
            detail: `$${formatMoney(source.incomePayStubGross)}`,
            matchMode: 'contains',
            locateKind: 'money'
        });
    }
    pushField(out, 'stubStart', 'Stub start', dateVariants(source.incomePayStubStartDate), {
        matchMode: 'contains'
    });
    pushField(out, 'stubEnd', 'Stub end', dateVariants(source.incomePayStubEndDate), {
        matchMode: 'contains'
    });
    if (source.receiptAmount) {
        pushField(out, 'receiptAmt', 'Amount', moneyVariants(source.receiptAmount), {
            detail: `$${formatMoney(source.receiptAmount)}`,
            matchMode: 'contains',
            locateKind: 'money'
        });
    }
    pushField(out, 'receiptDate', 'Date', dateVariants(source.receiptDate), {
        matchMode: 'contains'
    });
    pushField(out, 'email', 'Email', [source.email || ''], {});
    return out;
}
